// src/pages/Employees/index.tsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px); /* deja sitio para header/footer */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f9fafb;
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(23, 31, 114, 0.08);
  padding: 2.25rem;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 10px;
  }
`;

const Title = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 1.6rem;
  color: #222;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 1.25rem 0;
  color: #555;
  font-size: 0.95rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: #333;
  margin-bottom: 0.375rem;
`;

const Input = styled.input<{ invalid?: boolean }>`
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.invalid ? "#ff6b6b" : "#e6e9ef")};
  outline: none;
  font-size: 1rem;
  transition: border 0.15s ease-in-out, box-shadow 0.15s;
  box-shadow: ${(p) => (p.invalid ? "0 0 0 3px rgba(255,107,107,0.06)" : "none")};

  &:focus {
    border-color: #2e186a;
    box-shadow: 0 6px 20px rgba(46,24,106,0.08);
  }

  &::placeholder {
    color: #9aa0b4;
  }
`;

const ErrorText = styled.span`
  color: #ff6b6b;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.6rem;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
`;

const SmallText = styled.span`
  font-size: 0.85rem;
  color: #666;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  background: ${(p) => (p.disabled ? "#cfcfd6" : "#2e186a")};
  color: ${(p) => (p.disabled ? "#7a7a85" : "#fff")};
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  min-width: 160px;
  transition: background 0.18s ease-in-out, transform 0.08s;

  &:hover {
    transform: ${(p) => (p.disabled ? "none" : "translateY(-1px)")};
  }
`;

/* -----------------------
   Reglas de validación
   -----------------------
   - username: solo letras y números, 3-30 chars
   - password: 8-64, permite símbolos seguros
*/
const USERNAME_REGEX = /^[A-Za-z0-9.]{8,20}$/;
const PASSWORD_REGEX = /^\S{8,}$/; // mínimo 8 caracteres, sin espacios

const Empleados: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);


  const usernameValid = USERNAME_REGEX.test(username);
  const passwordValid = PASSWORD_REGEX.test(password);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUsernameTouched(true);
    setPasswordTouched(true);
    setServerError(null);

    if (!usernameValid || !passwordValid) return;

    try {
      setSubmitting(true);
      const res = await fetch("https://kevhel.com/api/login.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
        credentials: "include"
      });
      const data = await res.json();

      if (!data.success) {
        setServerError(data.message || "Usuario o contraseña incorrectos");
      } else {
        if (data.role === "admin") {
          window.location.href = "/empleados/admin";
        } else {
          window.location.href = "/empleados/dashboard";
        }
      }
    } catch (err) {
      setServerError("Error en la conexión. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Card>
        <Title>Acceso empleados</Title>
        <Subtitle>Introduce tu usuario y contraseña para acceder al área de empleados.</Subtitle>

        <Form onSubmit={onSubmit} noValidate>
          <Field>
            <Label htmlFor="username">Usuario</Label>
            <Input
              id="username"
              name="username"
              placeholder="Introduce nombre de usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onBlur={() => setUsernameTouched(true)}
              invalid={usernameTouched && !usernameValid}
              autoComplete="username"
              inputMode="text"
            />
            {usernameTouched && !usernameValid && (
              <ErrorText>
                El usuario debe tener entre 8 y 20 caracteres y solo puede contener letras, números o "."
              </ErrorText>
            )}
          </Field>

          <Field>
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              name="password"
              placeholder="Introduce contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setPasswordTouched(true)}
              invalid={passwordTouched && !passwordValid}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
              style={{
                right: "0.6rem",
                marginTop: "0.4rem",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "0.85rem",
                color: "#777",
                transition: "color 0.2s ease, transform 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#2e186a";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#777";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >

              {showPassword ? "ocultar contraseña" : "mostrar contraseña"}
            </button>
            {passwordTouched && !passwordValid && (
              <ErrorText>
                La contraseña debe tener más de 8 caracteres y no puede contener espacios.
              </ErrorText>
            )}
          </Field>


          {serverError && <ErrorText>{serverError}</ErrorText>}

          <Actions>
            <SubmitButton type="submit" disabled={!usernameValid || !passwordValid || submitting}>
              {submitting ? "Accediendo..." : "Acceso empleado"}
            </SubmitButton>
          </Actions>
        </Form>
      </Card>
    </PageWrapper>
  );
};

export default Empleados;